import React, { useRef, useEffect, useState } from "react";
import { renderMarkdown } from "../../helpers/renderMarkdown";
import { formatTimestamp } from "../../helpers/formatTimestamp";
import { FaRegUser, FaExpand, FaCompress, FaStar, FaRegStar } from "react-icons/fa6";
import { TiTick } from "react-icons/ti";
import { downloadFile } from '../../services/downloadService';

import copiaAIIcon from "../../images/CopiaAi-Logo.png"
import TableModal from "./TableModal";
import DataSummaryCard from "./DataSummaryCard";
import "./markdown.scss";

function ChatMessages({
    conversation,
    onMarkMessagesRead,
    isLoading,
    isStreaming,
    streamingMessageIndex,
    mcpLoadingState,
    suggestedResponses,
    onSuggestedResponse,
    isDownloading
}) {
    const chatContainerRef = useRef(null);
    const messageEndRef = useRef(null);
    const [expandedTable, setExpandedTable] = useState(null);
    const [modalData, setModalData] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [messageRatings, setMessageRatings] = useState({});
    const [lastConversationLength, setLastConversationLength] = useState(0);

    useEffect(() => {
        if (conversation.length > lastConversationLength && messageEndRef.current) {
            messageEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
        setLastConversationLength(conversation.length);
    }, [conversation, lastConversationLength]);

    const processMessageText = (text, messageObj = null) => {
        let processedText = text
            .replace(/\[\{mcp_call::[^}]*\}\]/g, '')
            .replace(/\[\{mcp_call_complete::[^}]*\}\]/g, '')
            .replace(/\[\{mcp_call\}\]/g, '')
            .replace(/\[\{mcp_call_complete\}\]/g, '')
            .replace(/\[\{mcp_call_failed\}\]/g, '')
            .replace(/\[\{mcp_list_tools\}\]/g, '')
            .replace(/\[\{mcp_list_tools_complete\}\]/g, '')
            .replace(/\[\{mcp_list_tools_failed\}\]/g, '');

        const logIdPattern = /\[\{LOG_ID:(\d+)\}\]/g;
        let logIdMatch = logIdPattern.exec(processedText);
        if (logIdMatch && messageObj) {
            messageObj.logId = parseInt(logIdMatch[1]);
        }

        processedText = processedText.replace(/\[\{LOG_ID:\d+\}\]/g, '');

        const downloadInfoPattern = /\[\{DOWNLOAD_INFO:\['([^']+)'\]\}/g;
        let downloadMatch;
        if (messageObj && (downloadMatch = downloadInfoPattern.exec(processedText))) {
            messageObj.downloadUrl = downloadMatch[1];
        }

        processedText = processedText.replace(/\[\{DOWNLOAD_INFO:\['[^']+'\]\}/g, '');

        return processedText;
    };

    const renderEnhancedMarkdown = (text, messageObj = null) => {
        const processedText = processMessageText(text, messageObj);
        let html = renderMarkdown(processedText).__html;

        html = html.replace(
            /<table>/g,
            '<div class="table-wrapper"><table>'
        );
        html = html.replace(
            /<\/table>/g,
            '</table></div>'
        );

        return { __html: html };
    };

    const renderStreamingContent = (text, isCurrentlyStreaming, messageObj = null) => {
        if (!isCurrentlyStreaming) {
            return renderEnhancedMarkdown(text, messageObj);
        }
        return renderEnhancedMarkdown(text + '▋', messageObj);
    };

    useEffect(() => {
        const updateScrollIndicators = () => {
            const tableWrappers = document.querySelectorAll('.table-wrapper');
            tableWrappers.forEach(wrapper => {
                const { scrollLeft, scrollWidth, clientWidth } = wrapper;

                wrapper.classList.toggle('scrollable-left', scrollLeft > 0);
                wrapper.classList.toggle('scrollable-right',
                    scrollLeft < scrollWidth - clientWidth - 1);
            });
        };

        const tableWrappers = document.querySelectorAll('.table-wrapper');
        tableWrappers.forEach(wrapper => {
            wrapper.addEventListener('scroll', updateScrollIndicators);
            updateScrollIndicators();
        });

        return () => {
            tableWrappers.forEach(wrapper => {
                wrapper.removeEventListener('scroll', updateScrollIndicators);
            });
        };
    }, [conversation]);

    const handleContainerClick = () => {
        if (onMarkMessagesRead) onMarkMessagesRead();
    };

    const handleDownloadClick = async (fileId) => {
        try {
            if (isDownloading) {
                isDownloading.current = true;
            }

            const result = await downloadFile(fileId);

            if (!result.success) {
                throw new Error(result.message);
            }

        } catch (error) {
            console.error("Download error:", error);
            alert("Download failed: " + error.message);
        } finally {
            if (isDownloading) {
                setTimeout(() => {
                    isDownloading.current = false;
                }, 1000);
            }
        }
    };

    const handleTableExpand = (messageIndex) => {
        setExpandedTable(expandedTable === messageIndex ? null : messageIndex);
    };

    const handleModalOpen = (data, title) => {
        setModalData({ data, title });
        setShowModal(true);
    };

    const handleModalClose = () => {
        setShowModal(false);
        setModalData(null);
    };

    const handleRating = async (logId, rating) => {
        try {
            const token = localStorage.getItem('copia_auth_token');

            const response = await fetch("https://copiaai.c247.tools/api/chat/rating", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    logId: logId,
                    rating: rating
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            if (result.success) {
                setMessageRatings(prev => ({ ...prev, [logId]: rating }));
            }
        } catch (error) {
            console.error("Rating error:", error);
        }
    };

    const renderStars = (logId, currentRating) => {
        return (
            <div className="rating-stars" style={{ display: 'flex', gap: '2px', marginTop: '5px' }}>
                {[1, 2, 3, 4, 5].map(star => (
                    <button
                        key={star}
                        onClick={() => handleRating(logId, star)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: star <= currentRating ? '#CB2C84' : 'black',
                            cursor: 'pointer',
                            fontSize: '12px',
                            padding: '2px'
                        }}
                    >
                        {star <= currentRating ? <FaStar /> : <FaRegStar />}
                    </button>
                ))}
            </div>
        );
    };

    const styles = {
        chatContainer: {
            flex: 1,
            overflowY: "auto",
            padding: "15px",
            backgroundColor: "#f8f9fa"
        },
        systemMessage: {
            backgroundColor: "#f0f0f0",
            color: "#666",
            borderRadius: "10px",
            padding: "8px 12px",
            maxWidth: "90%",
            margin: "0 auto",
            fontSize: "0.85rem",
            textAlign: "center",
            boxShadow: "none",
            animation: "fadeIn 0.3s"
        },
        userMessage: {
            backgroundColor: "#0c022f",
            color: "white",
            borderRadius: "18px 18px 0 18px",
            padding: "12px 16px",
            marginLeft: "0.5em",
            maxWidth: "80%",
            boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
            wordBreak: "break-word",
            position: "relative",
            animation: "fadeInUp 0.3s"
        },
        botMessage: {
            backgroundColor: "#e9ecef",
            color: "#212529",
            borderRadius: "18px 18px 18px 0",
            padding: "12px 16px",
            maxWidth: expandedTable !== null ? "95%" : "80%",
            marginRight: "auto",
            marginLeft: "0.5em",
            boxShadow: "0 2px 5px rgba(0, 0, 0, 0.05)",
            wordBreak: "break-word",
            position: "relative",
            animation: "fadeInUp 0.3s",
            transition: "max-width 0.3s ease"
        },
        errorMessage: {
            backgroundColor: "#fff8f8",
            color: "#d32f2f",
            borderRadius: "18px 18px 18px 0",
            marginLeft: "0.5em",
            padding: "12px 16px",
            border: "1px solid #ffcdd2",
            maxWidth: "80%",
            marginRight: "auto"
        },
        iconContainer: {
            width: "25px",
            height: "25px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%"
        },
        userIcon: { backgroundColor: "#0c022f" },
        timestamp: {
            fontSize: "10px",
            color: "#999",
            marginTop: "4px",
            display: "block",
            textAlign: "right"
        },
        readReceipt: {
            fontSize: "10px",
            color: "#999",
            marginLeft: "4px",
            display: "inline-block"
        },
        typingAnimation: {
            display: "flex",
            alignItems: "center",
            padding: "10px 16px",
            borderRadius: "18px 18px 18px 0",
            backgroundColor: "#e9ecef",
            maxWidth: "80px",
            marginRight: "auto",
            marginBottom: "10px",
            animation: "fadeIn 0.3s"
        },
        typingDot: {
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            backgroundColor: "#666",
            margin: "0 3px",
            animation: "typingAnimation 1.4s infinite ease-in-out"
        },
        suggestedResponsesContainer: {
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            marginTop: "15px",
            marginBottom: "10px",
            justifyContent: "center",
            animation: "fadeIn 0.5s"
        },
        suggestedResponse: {
            backgroundColor: "#CB2C84",
            color: "white",
            border: "none",
            borderRadius: "18px",
            padding: "8px 16px",
            fontSize: "12px",
            cursor: "pointer",
            whiteSpace: "nowrap",
            transition: "transform 0.2s, opacity 0.2s",
            opacity: 0.9
        },
        downloadInfo: {
            backgroundColor: "#e3f2fd",
            color: "#0c022f",
            borderRadius: "10px",
            padding: "10px",
            margin: "8px 0",
            fontSize: "13px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
        },
        downloadButton: {
            backgroundColor: "#0c022f",
            color: "white",
            border: "none",
            borderRadius: "4px",
            padding: "5px 10px",
            fontSize: "12px",
            cursor: "pointer"
        },
        robotImage: {
            width: "40px"
        },
        expandButton: {
            position: "absolute",
            top: "8px",
            right: "8px",
            background: "rgba(0,0,0,0.1)",
            border: "none",
            borderRadius: "4px",
            padding: "4px",
            cursor: "pointer",
            fontSize: "10px",
            color: "#666"
        }
    };

    return (
        <div
            style={styles.chatContainer}
            className="chatContainer"
            ref={chatContainerRef}
            onClick={handleContainerClick}
        >
            {conversation.map((msg, index) => {
                if (msg.isSystem) {
                    return (
                        <div key={index} className="my-2" style={styles.systemMessage}>
                            {msg.text}
                        </div>
                    );
                }

                const isUser = msg.sender === "You";
                const isExpanded = expandedTable === index;
                const hasTable = msg.text && msg.text.includes('|') && msg.text.includes('---');

                const messageStyle = isUser
                    ? styles.userMessage
                    : msg.isError
                        ? styles.errorMessage
                        : {
                            ...styles.botMessage,
                            maxWidth: isExpanded ? "95%" : "80%"
                        };

                return (
                    <div
                        key={index}
                        className={`d-flex mb-3 align-items-end ${isUser ? "justify-content-end" : "justify-content-start"
                            }`}
                    >
                        {!isUser && (
                            <div style={{ ...styles.iconContainer }} className="me-2">
                                <img
                                    src={copiaAIIcon}
                                    alt="Copia Robot"
                                    style={styles.robotImage}
                                />
                            </div>
                        )}

                        <div style={messageStyle}>
                            {!isUser && hasTable && (
                                <button
                                    style={styles.expandButton}
                                    onClick={() => handleTableExpand(index)}
                                    title={isExpanded ? "Compact view" : "Expand table"}
                                >
                                    {isExpanded ? <FaCompress /> : <FaExpand />}
                                </button>
                            )}

                            {msg.sender === "Copia" ? (
                                <div
                                    className={`message-content markdown-content ${isExpanded ? 'expanded' : ''} ${msg.isStreaming ? 'streaming' : ''
                                        }`}
                                    dangerouslySetInnerHTML={renderStreamingContent(
                                        msg.text,
                                        msg.isStreaming && isStreaming,
                                        msg
                                    )}
                                />
                            ) : (
                                <p className="mb-0">
                                    <strong>{msg.sender}:</strong> {msg.text}
                                </p>
                            )}

                            {msg.downloadInfo && (
                                <div style={styles.downloadInfo}>
                                    <span>{msg.downloadInfo.recordCount} records available</span>
                                    <button
                                        style={styles.downloadButton}
                                        onClick={() => handleDownloadClick(msg.downloadInfo.fileId)}
                                    >
                                        Download {msg.downloadInfo.format.toUpperCase()}
                                    </button>
                                </div>
                            )}

                            {msg.tableData && msg.tableData.length > 20 && (
                                <DataSummaryCard
                                    data={msg.tableData}
                                    title="Query Results"
                                    onViewFull={() => handleModalOpen(msg.tableData, "Query Results")}
                                />
                            )}

                            {!isUser && msg.downloadUrl && (
                                <div style={{ margin: "10px 0" }}>
                                    <button
                                        onClick={() => {
                                            if (isDownloading) {
                                                isDownloading.current = true;
                                            }

                                            const link = document.createElement('a');
                                            link.href = msg.downloadUrl;
                                            link.download = '';
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);

                                            if (isDownloading) {
                                                setTimeout(() => {
                                                    isDownloading.current = false;
                                                }, 1000);
                                            }
                                        }}
                                        style={{
                                            background: "#0c022f",
                                            color: "white",
                                            border: "none",
                                            padding: "10px 20px",
                                            borderRadius: "5px",
                                            cursor: "pointer",
                                            fontSize: "14px"
                                        }}
                                    >
                                        Download Data
                                    </button>
                                </div>
                            )}

                            {!isUser && msg.logId && (
                                renderStars(msg.logId, messageRatings[msg.logId] || 0)
                            )}

                            <span style={styles.timestamp}>
                                {formatTimestamp(msg.timestamp)}
                                {isUser && (
                                    <span style={styles.readReceipt}>
                                        {index < conversation.length - 1 &&
                                            conversation[index + 1].sender === "Copia" ? (
                                            <TiTick />
                                        ) : (
                                            "Sent"
                                        )}
                                    </span>
                                )}
                            </span>
                        </div>

                        {isUser && (
                            <div style={{ ...styles.iconContainer, ...styles.userIcon }} className="ms-2">
                                <FaRegUser color="white" />
                            </div>
                        )}
                    </div>
                );
            })}

            {mcpLoadingState && (
                <div className="d-flex align-items-end mb-3">
                    <div style={{ ...styles.iconContainer }} className="me-2">
                        <img
                            src={copiaAIIcon}
                            alt="Copia Robot"
                            style={styles.robotImage}
                        />
                    </div>
                    <div style={{
                        ...styles.botMessage,
                        backgroundColor: "#fff3cd",
                        border: "1px solid #ffeaa7",
                        fontStyle: "italic"
                    }}>
                        {mcpLoadingState}
                    </div>
                </div>
            )}

            {isLoading && !mcpLoadingState && (
                <div className="d-flex align-items-end mb-3">
                    <div style={{ ...styles.iconContainer }} className="me-2">
                        <img
                            src={copiaAIIcon}
                            alt="Copia Robot"
                            style={styles.robotImage}
                        />
                    </div>
                    <div style={styles.typingAnimation}>
                        <div className="typing-dot" style={styles.typingDot}></div>
                        <div className="typing-dot" style={styles.typingDot}></div>
                        <div className="typing-dot" style={styles.typingDot}></div>
                    </div>
                </div>
            )}

            {suggestedResponses.length > 0 && !isLoading && (
                <div style={styles.suggestedResponsesContainer}>
                    {suggestedResponses.map((suggestion, i) => (
                        <button
                            key={i}
                            onClick={() => onSuggestedResponse(suggestion)}
                            style={styles.suggestedResponse}
                            className="suggested-response"
                        >
                            {suggestion}
                        </button>
                    ))}
                </div>
            )}

            <div ref={messageEndRef} />

            <TableModal
                isOpen={showModal}
                onClose={handleModalClose}
                data={modalData?.data}
                title={modalData?.title}
            />
        </div>
    );
}

export default ChatMessages;